import { useQuery } from '@tanstack/react-query';
import { useOkapiKy } from '@folio/stripes/core';
import { useIntl } from 'react-intl';

const STALE_TIME = 30 * 60 * 1000;

/**
 * Fetches the field labels map from the backend for the current locale.
 *
 * @param {string} [fieldType] - Optional filter: 'table' | 'dimension' | 'measure'
 *                               When omitted, fetches all types.
 *
 * Returns { labelsMap, getLabel, isLoading }
 */
export default function useFieldLabelsAPI({ fieldType } = {}) {
  const okapiKy = useOkapiKy();
  const intl    = useIntl();
  const locale  = intl.locale || 'en';

  const params = new URLSearchParams();
  params.set('locale', locale);
  params.set('limit', '1000');
  if (fieldType) params.set('fieldType', fieldType);

  const { data: labelsMap, isLoading } = useQuery({
    queryKey: ['field-labels', locale, fieldType || 'all'],
    queryFn: () =>
      okapiKy
        .get(`field-labels?${params.toString()}`)
        .json()
        .then(res => {
          // Backend returns flat map when locale param is present
          if (res && typeof res === 'object' && !Array.isArray(res) && !res.fieldLabels) {
            return res;
          }
          // Fallback: build map from array response
          const map = {};
          (res?.fieldLabels || []).forEach(fl => {
            const entry = (fl.label || []).find(l =>
              l.locale === locale || l.locale?.startsWith(locale.split('-')[0])
            ) || fl.label?.[0];
            if (entry) map[fl.fieldKey] = entry.value;
          });
          return map;
        }),
    staleTime: STALE_TIME,
    retry: 1,
    onError: () => {},
  });

  /**
   * Resolve a human-readable label for a Cube.js field key.
   * Priority: aliasMember label → direct fieldKey label → fallback
   *
   * @param {string} fieldKey      - view field name or cube field key
   * @param {object} [fieldAliasMap] - optional { viewFieldName: aliasMember }
   */
  const getLabel = (fieldKey, fieldAliasMap) => {
    if (!fieldKey) return '';

    // 1. Try aliasMember from fieldAliasMap
    const alias = fieldAliasMap?.[fieldKey];
    if (alias && labelsMap?.[alias]) return labelsMap[alias];

    // 2. Try direct fieldKey
    if (labelsMap?.[fieldKey]) return labelsMap[fieldKey];

    // 3. Fallback: humanize
    const name = fieldKey.includes('.')
      ? fieldKey.substring(fieldKey.lastIndexOf('.') + 1)
      : fieldKey;
    return name
      .replace(/_/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .trim()
      .replace(/\b\w/g, c => c.toUpperCase());
  };

  return { labelsMap: labelsMap || {}, getLabel, isLoading };
}
